import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {ActiveJobsTable} from './ActiveJobsTable'
import {ModernDashboardWidget} from './ModernDashboardWidget'
import {ModernStatusChart} from './ModernStatusChart'
import {ModernAttendanceChart} from './ModernAttendanceChart'
import {WeeklyRevenueChart} from './WeeklyRevenueChart'
import {DashboardStatsTable} from './DashboardStatsTable'
import {OperationalInsightsCard} from './OperationalInsightsCard'
import {WaterWashCard} from './WaterWashCard'
import {JobsActivityCard} from './JobsActivityCard'
import {ActivityHeatMapCard} from './ActivityHeatMapCard'
import {getManagerDashboard, getManagerTableMetrics} from '../generic/crud'
import {JobCardDetailsDialog} from '../jobCards/JobCardDetailsDialog'
import {useAuth} from '../../modules/auth'
import {CacheTimerBadge} from './CacheTimerBadge'
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

export const ManagerDashboardPage = () => {
  const {mode} = useThemeMode()
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark')
  const {currentUser} = useAuth()
  const [activeJobs, setActiveJobs] = useState<any[]>([])
  const [closedTodayJobs, setClosedTodayJobs] = useState<any[]>([])
  const [allClosedJobs, setAllClosedJobs] = useState<any[]>([])
  const [attendanceData, setAttendanceData] = useState<any[]>([])
  const [myBranchIds, setMyBranchIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const [tableMetrics, setTableMetrics] = useState<any>(null)
  const [tableMetricsLoading, setTableMetricsLoading] = useState(true)
  const [countdown, setCountdown] = useState<number>(0)
  const [cacheExpiry, setCacheExpiry] = useState<string | null>(null)
  const [heatmapData, setHeatmapData] = useState<any[]>([])
  const [waterWash, setWaterWash] = useState<{today: number, thisWeek: number, thisMonth: number}>({today: 0, thisWeek: 0, thisMonth: 0})
  const [waterWashDates, setWaterWashDates] = useState<any[]>([])

  // Dialog state
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [detailsJobCardId, setDetailsJobCardId] = useState<string | null>(null)

  useEffect(() => {
    if (currentUser) {
      fetchDashboardData()
      fetchTableMetrics()
    }
  }, [refreshCounter, currentUser])

  // Countdown timer for cache expiry
  useEffect(() => {
    if (!cacheExpiry) return

    const updateCountdown = () => {
      const now = new Date().getTime()
      const expiry = new Date(cacheExpiry).getTime()
      const remaining = Math.max(0, Math.floor((expiry - now) / 1000))
      setCountdown(remaining)

      if (remaining <= 0) {
        clearInterval(interval)
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [cacheExpiry])

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const fetchDashboardData = async () => {
    const userId = currentUser?.userId;
    if (!userId) return;

    setLoading(true)
    try {
      // Single API call to fetch all manager dashboard data
      // Backend automatically filters by branches where user is manager
      const response = await getManagerDashboard()
      const data = response.data
      setCacheExpiry(data.cacheExpiry || null)
      setHeatmapData(data.heatmap || [])
      setWaterWash({today: data.waterWashToday || 0, thisWeek: data.waterWashThisWeek || 0, thisMonth: data.waterWashThisMonth || 0})
      setWaterWashDates(data.waterWashDates || [])

      // Check if user has any branches assigned
      if (data.message === 'No branches assigned') {
        setMyBranchIds([])
        setActiveJobs([])
        setClosedTodayJobs([])
        setAllClosedJobs([])
        setAttendanceData([])
        setLoading(false)
        return
      }

      // Extract branch IDs from branches data
      const branches = data.branches || []
      const branchIds = branches.map((b: any) => b.id)
      setMyBranchIds(branchIds)

      // Combine open and in-progress jobs for active jobs
      const openJobs = data.openJobs || []
      const inProgressJobs = data.inProgressJobs || []
      const allActiveJobs = [...openJobs, ...inProgressJobs]

      setActiveJobs(allActiveJobs)
      setClosedTodayJobs(data.closedToday || [])
      setAllClosedJobs(data.closedToday || []) // Use closedToday for all closed (contains 2 months data)
      setAttendanceData(data.todayAttendance || [])

      console.log('✅ Manager Dashboard data loaded in single API call')
      console.log('📊 My Branches:', branchIds.length)
      console.log('📊 Active Jobs:', allActiveJobs.length)
      console.log('📊 Closed Today:', data.closedToday?.length || 0)
      console.log('📊 Attendance:', data.todayAttendance?.length || 0)
    } catch (error) {
      console.error('❌ Error fetching manager dashboard data:', error)
      setActiveJobs([])
      setClosedTodayJobs([])
      setAllClosedJobs([])
      setAttendanceData([])
    } finally {
      setLoading(false)
    }
  }

  const fetchTableMetrics = async () => {
    setTableMetricsLoading(true)
    try {
      const response = await getManagerTableMetrics()
      setTableMetrics(response.data)
      console.log('✅ Table metrics loaded')
    } catch (error) {
      console.error('❌ Error fetching table metrics:', error)
      setTableMetrics(null)
    } finally {
      setTableMetricsLoading(false)
    }
  }

  // Calculate metrics
  const totalActiveJobs = activeJobs.length
  const todayClosedCount = closedTodayJobs.length

  const overdueJobs = activeJobs.filter((job: any) => {
    if (!job.committedDate) return false
    return new Date(job.committedDate) < new Date() && job.status !== 'CLOSED'
  }).length

  const workersPresent = new Set(attendanceData.map((a: any) => a.workerId)).size;

  const handleJobClick = (job: any) => {
    setDetailsJobCardId(job.id)
    setShowDetailsModal(true)
  }

  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1)
  }

  // Dummy trend data
  const jobsSeries = [{ name: 'Jobs', data: [totalActiveJobs > 3 ? totalActiveJobs - 3 : 0, totalActiveJobs, totalActiveJobs + 1, totalActiveJobs] }]
  const closedSeries = [{ name: 'Closed', data: [0, 1, todayClosedCount] }]
  const overdueSeries = [{ name: 'Overdue', data: [overdueJobs, overdueJobs + 1, overdueJobs] }]
  const workersSeries = [{ name: 'Workers', data: [workersPresent > 2 ? workersPresent - 2 : 0, workersPresent] }]


  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-6">
        <div>
          <h1 className="text-gray-900 fw-bolder fs-1 mb-2">Manager Dashboard</h1>
          <span className="text-muted fw-bold fs-6">
            Operations overview and team management
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {cacheExpiry && countdown > 0 && (
            <CacheTimerBadge countdown={countdown} formatCountdown={formatCountdown} />
          )}
          <Link className="btn btn-sm btn-primary d-none d-md-inline-block" to="/jobCards">
            <i className="bi bi-card-checklist me-2"></i>
            Create Job
          </Link>
          <Link className="btn btn-sm btn-light-primary d-none d-md-inline-block" to="/attendance/branches">
            <i className="bi bi-calendar-check me-2"></i>
            Attendance
          </Link>
          <button
            className="btn btn-sm btn-light-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise me-2 ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-6 col-md-6 col-xl-3">
          <ModernDashboardWidget
            title="Active Jobs"
            description="Open & In Progress"
            value={totalActiveJobs}
            icon="bi-list-task"
            color="#009ef7" // Blue
            chartSeries={jobsSeries}
            chartColor="#009ef7"
            onClick={() => document.getElementById('active-jobs-table')?.scrollIntoView({behavior: 'smooth'})}
          />
        </div>
        <div className="col-6 col-md-6 col-xl-3">
          <ModernDashboardWidget
            title="Completed Today"
            description="Finished jobs"
            value={todayClosedCount}
            icon="bi-check-circle-fill"
            color="#50cd89" // Green
            chartSeries={closedSeries}
            chartColor="#50cd89"
          />
        </div>
        <div className="col-6 col-md-6 col-xl-3">
          <ModernDashboardWidget
            title="Overdue Jobs"
            description="Needs attention"
            value={overdueJobs}
            icon="bi-exclamation-triangle-fill"
            color="#f1416c" // Red
            chartSeries={overdueSeries}
            chartColor="#f1416c"
          />
        </div>
        <div className="col-6 col-md-6 col-xl-3">
           <ModernDashboardWidget
            title="Workers Present"
            description="Team availability"
            value={workersPresent}
            icon="bi-people-fill"
            color="#ffc700" // Warning/Yellow
            chartSeries={workersSeries}
            chartColor="#ffc700"
          />
        </div>
      </div>

      {/* Row 2: Quick Actions */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10 d-md-none">
        <div className="col-6">
          <Link to="/jobCards" className="card h-100 bg-light-primary hover-elevate-up" style={{textDecoration: 'none', border: '2px solid #009ef7'}}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-5 p-md-9">
                <i className="bi bi-card-checklist fs-2x fs-md-3x text-primary mb-3 mb-md-5"></i>
                <div className="fs-4 fs-md-2 fw-bolder text-primary mb-1 mb-md-2">Create Job</div>
                <div className="fw-bold text-primary opacity-75 d-none d-md-block">Assign new tasks</div>
            </div>
          </Link>
        </div>
        <div className="col-6">
          <Link to="/attendance/branches" className="card h-100 bg-light-success hover-elevate-up" style={{textDecoration: 'none', border: '2px solid #50cd89'}}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-5 p-md-9">
                <i className="bi bi-calendar-check fs-2x fs-md-3x text-success mb-3 mb-md-5"></i>
                <div className="fs-4 fs-md-2 fw-bolder text-success mb-1 mb-md-2">Attendance</div>
                <div className="fw-bold text-success opacity-75 d-none d-md-block">Track presence</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Row 3: Charts */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-4">
           <WaterWashCard className="h-100" today={waterWash.today} thisWeek={waterWash.thisWeek} thisMonth={waterWash.thisMonth} />
        </div>
        <div className="col-xl-4">
           <JobsActivityCard
             className="h-100"
             activeJobs={activeJobs}
             closedJobs={allClosedJobs}
             loading={loading}
           />
        </div>
        <div className="col-xl-4">
            <ActivityHeatMapCard
                className="h-100"
                heatmap={heatmapData}
                loading={loading}
            />
        </div>
      </div>

      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-4">
          <WeeklyRevenueChart allClosedJobs={allClosedJobs} loading={loading} />
        </div>
        <div className="col-xl-4">
           <ModernStatusChart jobs={activeJobs} loading={loading} />
        </div>
        <div className="col-xl-4">
          <ModernAttendanceChart attendance={attendanceData} loading={loading} />
        </div>
      </div>

      {/* Row 4: Dashboard Stats Table */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-12">
            <DashboardStatsTable
              metrics={tableMetrics}
              loading={tableMetricsLoading}
            />
        </div>
      </div>

      {/* Row 5: Smart Operational Insights */}
      <OperationalInsightsCard
        allClosedJobs={allClosedJobs}
        allOpenJobs={activeJobs}
        allActiveJobs={activeJobs}
        loading={loading}
        role="manager"
        preloadedWaterWashDates={waterWashDates}
      />

      {/* Active Jobs Table */}
      <div id="active-jobs-table" className="mb-5 mb-xl-8">
        {/* @ts-ignore */}
        <ActiveJobsTable
          jobs={activeJobs}
          loading={loading}
          onJobClick={handleJobClick}
        />
      </div>

      {/* Job Details Dialog */}
      <JobCardDetailsDialog
        show={showDetailsModal}
        handleClose={() => setShowDetailsModal(false)}
        jobCardId={detailsJobCardId}
        onStatusChange={handleRefresh}
      />
    </>
  )
}